import React from "react";
import { Calendar, FileText, MicOff, Clock } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

const Features = () => {
  const { ref: sectionRef, isIntersecting: isVisible } =
    useIntersectionObserver();

  const features = [
    {
      icon: <Calendar className="h-8 w-8 text-perisalah-blue" />,
      title: "Transkripsi Otomatis",
      description:
        "Ubah rekaman rapat menjadi teks dengan akurasi tinggi secara otomatis. Didukung oleh teknologi AI mutakhir.",
    },
    {
      icon: <FileText className="h-8 w-8 text-perisalah-blue" />,
      title: "Dokumen Terstruktur",
      description:
        "Buat MoM, PRD, BRD, dan format dokumen lainnya dengan sekali klik. Semua dari hasil transkripsi rapat.",
    },
    {
      icon: <MicOff className="h-8 w-8 text-perisalah-blue" />,
      title: "Rekaman Offline & Online",
      description:
        "Rekam rapat dengan mulus, baik di ruang rapat fisik maupun saat konferensi video online.",
    },
    {
      icon: <Clock className="h-8 w-8 text-perisalah-blue" />,
      title: "Template Kustom",
      description:
        "Bagi pengguna Pro, buat dan simpan template dokumen kustom sesuai kebutuhan perusahaan Anda.",
    },
  ];

  return (
    <section
      id="fitur"
      className="py-20 relative overflow-hidden bg-perisalah-darkblue"
      ref={sectionRef}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-perisalah-darkblue/60 to-black z-0"></div>
      <div className="container mx-auto px-4">
        <div
          className={`text-center mb-16 ${
            isVisible ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Fitur Unggulan <span className="text-gradient">Perisalah</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Tingkatkan produktivitas rapat Anda dengan teknologi AI canggih yang
            mengubah percakapan menjadi dokumen bernilai
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-black/30 border border-white/10 backdrop-blur-sm rounded-xl transition-all hover:border-perisalah-purple/50 hover:bg-black/50 flex flex-col items-center p-6 ${
                isVisible
                  ? `animate-fade-in-up delay-${index + 1}00`
                  : "opacity-0"
              }`}
            >
              <div className="p-3 w-16 h-16 rounded-lg bg-perisalah-blue/10 flex items-center justify-center mb-4">
                {React.cloneElement(feature.icon, {
                  className: "h-10 w-10 text-perisalah-blue",
                })}
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white text-center">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-base text-center">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`mt-20 feature-gradient rounded-2xl p-8 md:p-12 ${
            isVisible ? "animate-fade-in-up delay-500" : "opacity-0"
          }`}
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                Bagaimana Perisalah Bekerja?
              </h3>
              <ol className="space-y-4">
                <li
                  className={`flex gap-4 ${
                    isVisible ? "animate-fade-in-left delay-100" : "opacity-0"
                  }`}
                >
                  <div className="bg-accent text-white w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-lg text-white">
                      Rekam Rapat
                    </h4>
                    <p className="text-gray-200">
                      Rekam rapat offline atau online melalui aplikasi Perisalah
                    </p>
                  </div>
                </li>
                <li
                  className={`flex gap-4 ${
                    isVisible ? "animate-fade-in-left delay-200" : "opacity-0"
                  }`}
                >
                  <div className="bg-accent text-white w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-lg text-white">
                      AI Transkripsi
                    </h4>
                    <p className="text-gray-200">
                      AI kami secara otomatis mengubah audio menjadi teks dengan
                      akurasi tinggi
                    </p>
                  </div>
                </li>
                <li
                  className={`flex gap-4 ${
                    isVisible ? "animate-fade-in-left delay-300" : "opacity-0"
                  }`}
                >
                  <div className="bg-accent text-white w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-lg text-white">
                      Strukturisasi Dokumen
                    </h4>
                    <p className="text-gray-200">
                      AI mengategorikan dan menyusun hasil transkripsi ke format
                      dokumen pilihan
                    </p>
                  </div>
                </li>
                <li
                  className={`flex gap-4 ${
                    isVisible ? "animate-fade-in-left delay-400" : "opacity-0"
                  }`}
                >
                  <div className="bg-accent text-white w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h4 className="font-medium text-lg text-white">
                      Edit dan Bagikan
                    </h4>
                    <p className="text-gray-200">
                      Tinjau, edit, dan bagikan dokumen final kepada semua
                      peserta rapat
                    </p>
                  </div>
                </li>
              </ol>
            </div>
            <div
              className={`bg-[#181e41] rounded-xl overflow-hidden shadow-lg ${
                isVisible ? "animate-fade-in-right delay-300" : "opacity-0"
              }`}
            >
              <div className="p-1">
                <div className="bg-[#181e41] rounded overflow-hidden border border-white/5">
                  <div className="flex items-center justify-between p-3 border-b border-white/5">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="text-sm text-gray-300">Perisalah App</div>
                    <div></div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-4">
                      <div className="bg-[#23284a] p-3 rounded-lg shadow-sm border border-white/10">
                        <div className="flex justify-between">
                          <h5 className="font-medium text-white">
                            Rapat Mingguan Tim Produk
                          </h5>
                          <span className="text-sm text-gray-400">
                            10:30 - 11:30
                          </span>
                        </div>
                        <div className="my-2 h-1 w-full bg-white/10"></div>
                        <div className="space-y-1 text-sm text-gray-300">
                          <p>• Diskusi fitur baru</p>
                          <p>• Review sprint sebelumnya</p>
                          <p>• Alokasi tugas sprint berikutnya</p>
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className="text-xs text-gray-400">
                            Transkripsi sedang berjalan...
                          </span>
                          <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                      <div className="bg-[#23284a] p-3 rounded-lg shadow-sm border border-white/10">
                        <div className="flex justify-between">
                          <h5 className="font-medium text-white">
                            Export Format
                          </h5>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div className="border border-accent/20 bg-accent/10 rounded p-2 text-center text-sm text-white">
                            <FileText className="h-4 w-4 mx-auto mb-1" />
                            MoM
                          </div>
                          <div className="border border-white/10 rounded p-2 text-center text-sm text-white/80">
                            <FileText className="h-4 w-4 mx-auto mb-1" />
                            PRD
                          </div>
                          <div className="border border-white/10 rounded p-2 text-center text-sm text-white/80">
                            <FileText className="h-4 w-4 mx-auto mb-1" />
                            BRD
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
